import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Apiurl from "../environment/Apiurl";
import {
  reconcileRolePages,
  ROLE_PAGE_ITEMS,
} from "../../utils/rolePagesMigration";

function AddRoles() {
  const navigate = useNavigate();
  const { id } = useParams();

  const categories = [
    {
      name: "Core Management",
      items: ["Dashboard", "Roles", "Users", "Doctors", "Appointments"],
    },
    {
      name: "Follow-up Call",
      items: ["Follow-up 1", "Follow-up 2", "Courtesy Call"],
    },
    {
      name: "HR Management",
      items: [
        "Staff",
        "Department",
        "Attendance",
        "Shift",
        "Leave Management",
        "Salary Details",
        "Holiday",
      ],
    },
    {
      name: "Fees Management",
      items: ["Treatment Charges", "Doctor Fee Master"],
    },
    {
      name: "Patients",
      items: ["Patients", "Yoga Patients", "Homeopathy Patients"],
    },
    {
      name: "Billing",
      items: [
        // "IN Billing", // temporarily disabled
        "OUT Billing",
      ],
    },
    {
      name: "Pharmacy",
      items: [
        "Medicine List",
        "Prescription",
        "Purchase Order",
        "Purchase List",
        "Sale Pharmacy",
        "Stock Management",
        "Supplier",
        "Reports Pharmacy",
      ],
    },
    {
      name: "Operations",
      items: [
        "Lab Management",
        "Expenses",
        "Hospital Maintenance",
        "Hospital Purchases",
      ],
    },
    {
      name: "AI & Reports",
      items: ["Chat", "Reports"],
    },
  ];

  const actions = ["All", "Add", "Edit", "View", "Delete", "Download"];

  const [permissions, setPermissions] = useState(() => {
    const initialPermissions = {};
    ROLE_PAGE_ITEMS.forEach((item) => {
      initialPermissions[item] = {
        All: false,
        Add: false,
        Edit: false,
        View: false,
        Delete: false,
        Download: false,
      };
    });
    return initialPermissions;
  });

  const [roleName, setRoleName] = useState("");
  const [privillages, setPrivillages] = useState({
    role_name: "",
    pages: ROLE_PAGE_ITEMS.map((item, index) => ({
      page: item,
      id: String(index + 1).padStart(3, "0"),
      access: false,
      priviallages: {
        all: false,
        create: false,
        read: false,
        update: false,
        delete: false,
        download: false,
      },
    })),
  });
  const [loading, setLoading] = useState(false);

  const handleCheckboxChange = (item, action, isChecked) => {
    setPermissions((prevPermissions) => {
      const newPermissions = { ...prevPermissions };

      if (!newPermissions[item]) {
        newPermissions[item] = {
          All: false,
          Add: false,
          Edit: false,
          View: false,
          Delete: false,
          Download: false,
        };
      }

      if (action === "All") {
        Object.keys(newPermissions[item]).forEach((key) => {
          if (key !== "All") {
            newPermissions[item][key] = isChecked;
          }
        });
        newPermissions[item].All = isChecked;
      } else {
        newPermissions[item][action] = isChecked;
        const allChecked = actions
          .slice(1)
          .every((act) => newPermissions[item][act]);
        newPermissions[item].All = allChecked;
      }

      return newPermissions;
    });

    setPrivillages((prevPrivillages) => {
      const index = prevPrivillages.pages.findIndex(
        (data) => data.page === item
      );
      if (index === -1) {
        console.error(`Page "${item}" not found in privillages.pages`);
        return prevPrivillages;
      }

      const actionMap = {
        Add: "create",
        View: "read",
        Edit: "update",
        Delete: "delete",
        All: "all",
        Download: "download",
      };

      const actionKey = actionMap[action];
      const newPrivillages = { ...prevPrivillages };
      newPrivillages.pages = [...prevPrivillages.pages];
      newPrivillages.pages[index] = { ...newPrivillages.pages[index] };
      newPrivillages.pages[index].priviallages = {
        ...newPrivillages.pages[index].priviallages,
      };

      if (isChecked) {
        newPrivillages.pages[index].access = true;
        newPrivillages.pages[index].priviallages[actionKey] = true;

        if (action === "All") {
          newPrivillages.pages[index].priviallages.create = true;
          newPrivillages.pages[index].priviallages.read = true;
          newPrivillages.pages[index].priviallages.update = true;
          newPrivillages.pages[index].priviallages.delete = true;
          newPrivillages.pages[index].priviallages.download = true;
        }
      } else {
        newPrivillages.pages[index].priviallages[actionKey] = false;

        if (action === "All") {
          newPrivillages.pages[index].priviallages.create = false;
          newPrivillages.pages[index].priviallages.read = false;
          newPrivillages.pages[index].priviallages.update = false;
          newPrivillages.pages[index].priviallages.delete = false;
          newPrivillages.pages[index].priviallages.download = false;
        }

        if (
          Object.values(newPrivillages.pages[index].priviallages).every(
            (val) => val === false
          )
        ) {
          newPrivillages.pages[index].access = false;
        }
      }

      return newPrivillages;
    });
  };

  const mapBackendToPermissions = (pages) => {
    const mapped = {};
    pages.forEach((page) => {
      mapped[page.page] = {
        All: page.priviallages?.all || false,
        Add: page.priviallages?.create || false,
        Edit: page.priviallages?.update || false,
        View: page.priviallages?.read || false,
        Delete: page.priviallages?.delete || false,
        Download: page.priviallages?.download || false,
      };
    });
    return mapped;
  };

  useEffect(() => {
    if (id) {
      const getPrivileges = async () => {
        setLoading(true);
        try {
          const response = await axios.get(
            `${Apiurl}/roles_and_privillage/${id}`
          );
          if (response.data) {
            const reconciledPages = reconcileRolePages(
              response.data.pages,
              ROLE_PAGE_ITEMS
            );
            setRoleName(response.data.role_name);
            setPrivillages({
              role_name: response.data.role_name,
              pages: reconciledPages,
            });
            setPermissions((prev) => ({
              ...prev,
              ...mapBackendToPermissions(reconciledPages),
            }));
          }
        } catch (error) {
          console.error("Error fetching privileges:", error);
        } finally {
          setLoading(false);
        }
      };
      getPrivileges();
    }
  }, [id]);

  const postRoles = async () => {
    if (!roleName.trim()) {
      alert("Role name is required!");
      return;
    }

    const updatedPrivillages = {
      ...privillages,
      role_name: roleName,
    };

    setLoading(true);
    try {
      let response;
      if (id) {
        response = await axios.put(
          `${Apiurl}/roles_and_privillage/${id}`,
          updatedPrivillages
        );
      } else {
        response = await axios.post(
          `${Apiurl}/roles_and_privillage`,
          updatedPrivillages
        );
      }

      if (response.status === 200 || response.status === 201) {
        navigate("/roles");
      } else {
        alert("Failed to save role. Please try again.");
      }
    } catch (error) {
      console.error("Error saving privileges:", error);
      alert("An error occurred while saving the role. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between gap-2">
        <div className="card card-body mb-2">
          <div className="row align-items-center">
            <div className="col-auto">
              <label htmlFor="roleName" className="col-form-label">
                Role Name:
              </label>
            </div>
            <div className="col">
              <input
                id="roleName"
                className="form-control mt-2"
                type="text"
                placeholder="Enter Your Role"
                onChange={(e) => setRoleName(e.target.value)}
                value={roleName}
              />
            </div>
          </div>
        </div>
        <button
          className="btn btn-secondary btn-sm mb-2 h-50 mt-4"
          onClick={() => navigate("/roles")}
          disabled={loading}
        >
          <i className="bi bi-arrow-left me-1"></i> Back
        </button>
      </div>
      <div className="card card-body shadow-sm">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-bordered table-hover">
              <thead className="thead-light">
                <tr>
                  <th>Modules</th>
                  {actions.map((action, index) => (
                    <th key={index} className="text-center">
                      {action}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {categories.map((category, catIndex) => (
                  <React.Fragment key={category.name}>
                    <tr className="role-category-row">
                      <td colSpan={actions.length + 1}>
                        <div className="role-category-title">{category.name}</div>
                      </td>
                    </tr>
                    {category.items.map((item, itemIndex) => (
                      <tr key={`${catIndex}-${itemIndex}`}>
                        <td>{item}</td>
                        {actions.map((action, actionIndex) => (
                          <td key={actionIndex} className="text-center">
                            <div className="form-check d-flex justify-content-center">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`${item}-${action}`}
                                checked={permissions[item]?.[action] || false}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    item,
                                    action,
                                    e.target.checked
                                  )
                                }
                                disabled={loading}
                              />
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-end mt-4">
            <button
              className="btn btn-sm btn-secondary me-2"
              onClick={() => navigate("/roles")}
              disabled={loading}
            >
              <i className="fas fa-times me-2"></i>Cancel
            </button>
            <button
              className="btn btn-sm text-white"
              style={{ backgroundColor: "#01AEBE" }}
              onClick={postRoles}
              disabled={loading}
            >
              <i className="fas fa-save me-2"></i>
              {loading ? "Saving..." : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddRoles;
